import {ColorSchemeType, Flag, Parameter, Parameters} from 'platform/components';
import {
  Heading,
  HStack,
  Icon,
  Link,
  Show,
  Space,
  ThemeColorPath,
  ThemeIconKey,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNotNil, map, pipe, reject} from 'ramda';

import {
  IntegrationAction,
  IntegrationActionKey,
  IntegrationCode,
  IntegrationLevel,
  ServiceOrderIntegration,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import {Nullish, TestIdProps, buildArray, parseDate, suffixTestId} from 'shared/web';

import {
  IntegrationCard,
  IntegrationCardAction,
} from '../../../../../components/IntegrationCard/IntegrationCard';
import {getExternalStatusLabel} from '../utils/getExternalStatusLabel';

interface IntegrationItemProps extends TestIdProps {
  integration: ServiceOrderIntegration;
  isActionPending: (key: string) => boolean;
  onActionClick: (actionKey?: IntegrationActionKey) => void;
  isExternalActionPending?: boolean;
}

export function IntegrationItem(props: IntegrationItemProps) {
  const formatDateTime = useDateTimeFormatter();

  const isInactive = !props.integration?.integration?.createdAt;

  const actions = pipe(
    reject<IntegrationAction>((action) => action?.style === 'hidden'),
    map<IntegrationAction, IntegrationCardAction>((action: IntegrationAction) => ({
      key: action?.key,
      title: getActionTitle(action?.key),
      icon: getActionIcon(action?.key),
      isLoading: props.isActionPending(`order/integration/${action?.key}`),
      isDisabled: action?.style === 'disabled' || props.isExternalActionPending,
      onClick: (e) => {
        e.stopPropagation();
        props.onActionClick(action?.key);
      },
    }))
  )(props.integration?.action ?? []);

  const color = match<IntegrationLevel | Nullish, ThemeColorPath | undefined>(
    props.integration?.notification?.level
  )
    .with('info', always('severity.informational'))
    .with('warning', always('severity.warning'))
    .with('error', always('severity.danger'))
    .with('success', always('severity.success'))
    .otherwise(always(undefined));

  const icon = match<IntegrationLevel | Nullish, ThemeIconKey | undefined>(
    props.integration?.notification?.level
  )
    .with('info', always('action/info'))
    .with('warning', always('alert/warning'))
    .with('error', always('alert/error'))
    .with('success', always('action/check_circle'))
    .otherwise(always(undefined));

  return (
    <IntegrationCard
      key={props.integration?.integration?.code}
      title={
        <HStack align="center" spacing={2}>
          <Heading size={4}>
            {getIntegrationTitle(props.integration?.integration?.code as IntegrationCode)}
          </Heading>
          <Show when={props.integration?.externalStatus?.status}>
            <Flag
              size="small"
              label={getExternalStatusLabel(props.integration?.externalStatus?.status)}
              colorScheme={match<IntegrationLevel | Nullish, ColorSchemeType | undefined>(
                props.integration?.externalStatus?.level
              )
                .with('error', always('red'))
                .with('warning', always('orange'))
                .with('success', always('green'))
                .with('info', always('blue'))
                .otherwise(always(undefined))}
            />
          </Show>
        </HStack>
      }
      actions={actions}
      isInactive={isInactive}
      data-testid={suffixTestId('card', props)}
    >
      <Show
        when={
          props.integration?.notification?.content ||
          props.integration?.integration?.createdAt ||
          props.integration?.integration?.updatedAt
        }
      >
        <HStack align="center">
          <Show when={props.integration?.notification?.level && isNotNil(icon) && isNotNil(color)}>
            <Icon size={4} value={icon} color={color} />
            <Space horizontal={1} />
          </Show>
          <Parameters
            color="secondary"
            size="xSmall"
            parameters={buildArray<Parameter>()
              .when(
                props.integration?.notification?.content,
                props.integration?.notification?.content
              )
              .when(
                props.integration?.integration?.createdAt,
                i18n.t('entity.orderIntegration.labels.sent', {
                  date:
                    props.integration?.integration?.createdAt &&
                    formatDateTime(
                      'dateTimeShort',
                      parseDate(props.integration?.integration?.createdAt)
                    ),
                })
              )
              .when(
                props.integration?.integration?.updatedAt,
                i18n.t('entity.orderIntegration.labels.updated', {
                  date:
                    props.integration?.integration?.updatedAt &&
                    formatDateTime(
                      'dateTimeShort',
                      parseDate(props.integration?.integration?.updatedAt)
                    ),
                })
              )}
          />
        </HStack>
      </Show>
      <Show
        when={
          props.integration?.integration?.viewDetailsLink &&
          props.integration?.integration?.createdAt
        }
      >
        <HStack align="center">
          <Space fillAvailable />
          <Link
            size="small"
            title={i18n.t('general.actions.viewDetail')}
            href={decodeURIComponent(props.integration?.integration?.viewDetailsLink ?? '')}
            target="_blank"
            data-testid={suffixTestId('view-detail', props)}
          />
        </HStack>
      </Show>
    </IntegrationCard>
  );
}

const getActionIcon = (key?: IntegrationActionKey) =>
  match<IntegrationActionKey | undefined, ThemeIconKey | undefined>(key)
    .with(
      undefined,
      'audatexTaskCalculations',
      'silverdatMyclaimContractCalculations',
      always(undefined)
    )
    .with(
      'audatexTaskResend',
      'chronosContractResend',
      'hyundaiHapReceiveResend',
      'silverdatMyclaimContractResend',
      always('action/update')
    )
    .with(
      'hyundaiHapReceiveRetrieve',
      'xentryPartsJobRetrieve',
      'xentryOperationTimeRetrieve',
      'tecrmiGetBasket',
      'daimlerXentryPartsJobRetrieve',
      'hyundaiAs1qRetrieve',
      'sagEshopRetrieve',
      'mazdaGepcRetrieve',
      always('file/download')
    )
    .with(
      'audatexTaskSend',
      'chronosContractSend',
      'hyundaiHapReceiveSend',
      'silverdatMyclaimContractSend',
      'xentryPartsJobSend',
      'xentryOperationTimeSend',
      'tecrmiSend',
      'daimlerClaraClaimSend',
      'daimlerXentryPartsJobSend',
      'hyundaiAs1qSend',
      'sagEshopSend',
      'mazdaGepcSend',
      always('content/send')
    )
    .with('chronosContractReport', always('action/info_outline'))
    .with('daimlerClaraClaimDetail', always('view'))
    .otherwise(always(undefined));

const getActionTitle = (key?: IntegrationActionKey) =>
  match<IntegrationActionKey | undefined, string>(key)
    .with(undefined, always(''))
    .with(
      'audatexTaskResend',
      'chronosContractResend',
      'hyundaiHapReceiveResend',
      'silverdatMyclaimContractResend',
      always(i18n.t('entity.orderIntegration.actions.resend'))
    )
    .with(
      'hyundaiHapReceiveRetrieve',
      'xentryPartsJobRetrieve',
      'xentryOperationTimeRetrieve',
      'tecrmiGetBasket',
      'daimlerXentryPartsJobRetrieve',
      'hyundaiAs1qRetrieve',
      'sagEshopRetrieve',
      'mazdaGepcRetrieve',
      always(i18n.t('entity.orderIntegration.actions.retrieve'))
    )
    .with('audatexTaskCalculations', always(i18n.t('entity.orderIntegration.actions.calculations')))
    .with(
      'audatexTaskSend',
      'chronosContractSend',
      'hyundaiHapReceiveSend',
      'silverdatMyclaimContractSend',
      'xentryPartsJobSend',
      'xentryOperationTimeSend',
      'tecrmiSend',
      'daimlerClaraClaimSend',
      'daimlerXentryPartsJobSend',
      'hyundaiAs1qSend',
      'sagEshopSend',
      'mazdaGepcSend',
      always(i18n.t('entity.orderIntegration.actions.send'))
    )
    .with('chronosContractReport', always(i18n.t('entity.orderIntegration.actions.report')))
    .with(
      'silverdatMyclaimContractCalculations',
      always(i18n.t('entity.orderIntegration.actions.calculations'))
    )
    .with('daimlerClaraClaimDetail', always(i18n.t('general.actions.viewDetail')))
    .otherwise((key) => key);

const getIntegrationTitle = (key?: IntegrationCode) =>
  match<IntegrationCode | undefined, string>(key)
    .with(undefined, always(''))
    .with('AUDATEX', always(i18n.t('entity.orderIntegration.labels.audatex')))
    .with('CHRONOS', always(i18n.t('entity.orderIntegration.labels.chronos')))
    .with('HYUNDAI_HAP', always(i18n.t('entity.orderIntegration.labels.hyundai')))
    .with('SILVERDAT_MYCLAIM', always(i18n.t('entity.orderIntegration.labels.silverdatMyclaim')))
    .with('XENTRY-PARTS-JOB', always(i18n.t('entity.orderIntegration.labels.xentryPartsJob')))
    .with(
      'DAIMLER-XENTRY-PARTS-JOB',
      always(i18n.t('entity.orderIntegration.labels.daimlerXentryPartsJob'))
    )
    .with(
      'XENTRY-OPERATION-TIME',
      always(i18n.t('entity.orderIntegration.labels.xentryOperationTime'))
    )
    .with('TECRMI', always(i18n.t('entity.orderIntegration.labels.tecRMI')))
    .with('DAIMLER_CLARA', always(i18n.t('entity.orderIntegration.labels.daimlerClara')))
    .with('SAG', always(i18n.t('entity.orderIntegration.labels.sagEshop')))
    .with('HYUNDAI_AS1Q', always(i18n.t('entity.orderIntegration.labels.hyundaiAS1Q')))
    .with('MAZDA_PORTAL', always(i18n.t('entity.orderIntegration.labels.mazdaPortal')))
    .otherwise((key) => key);
